import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the PDP specification sheet in sync with the selected variant.
 *
 * The variant picker morphs only itself, but the fetch it makes returns the WHOLE product page for
 * the new variant — so the freshly rendered sheet (variant SKU, selected option values, per-variant
 * materials) is already sitting in `detail.html`. We morph our own list from it instead of
 * re-deriving anything client side; there is no second request and no duplicated Liquid logic.
 *
 * Only the `<dl>` is morphed, never the `<details>` elements around it, so an accordion the shopper
 * opened stays open across variant changes.
 */
class R69ProductSpecs extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.#section = this.closest('[id*="ProductInformation-"]');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    // The open state is mirrored as `r69spec--open` on each <details> (Liquid renders it for the
    // initially-open ones). The stylesheet keys the ::details-content rules on that class, not on
    // `[open]`: a pseudo-element subject has no feature Blink can key on, so `[open]::details-content`
    // made the invalidation set of the `open` ATTRIBUTE "whole subtree" for the entire document —
    // every open/close of any details or drawer on the site restyled its whole subtree (closing
    // the filters drawer: 789 elements, 72–81 ms at CPU ×4 on a Pixel-class phone,
    // audit/2026-09/invalidation-rule-bisect-2026-09-06.mjs, 06.09.2026). `toggle` does not
    // bubble, hence capture.
    this.addEventListener('toggle', this.#mirrorOpen, true);

    // Enable the accordion open/close animation only AFTER first paint. The accordion is open by
    // default; with the transition live on load it animates block-size 0 -> auto (0.34s), shifting
    // everything below ~128px on reload. Two rAFs guarantee we are past the initial layout/paint.
    requestAnimationFrame(() => requestAnimationFrame(() => this.setAttribute('data-ready', '')));
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.removeEventListener('toggle', this.#mirrorOpen, true);
  }

  /** @param {Event} event */
  #mirrorOpen = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('r69spec__acc')) return;
    details.classList.toggle('r69spec--open', details.open);
  };

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const current = this.querySelector('[data-r69spec-list]');
        const updated = detail.html.querySelector(
          `r69-product-specs[data-block-id="${this.dataset.blockId}"] [data-r69spec-list]`
        );

        if (current && updated) morph(current, updated);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[r69-specs] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('r69-product-specs')) {
  customElements.define('r69-product-specs', R69ProductSpecs);
}
